import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { INDUSTRIES, WEBSITE_TEMPLATES, renderWebsiteHtml, type Website as Site, type WebsiteTemplate } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { useWebsite } from '../lib/hooks.js';
import { Badge, Button, Card, Field, Input, Select, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label } from '../lib/format.js';

const TEMPLATE_DESC: Record<WebsiteTemplate, string> = {
  modern: 'Clean, light, friendly — great all-rounder.',
  bold: 'High-contrast gradient hero, big type — stands out.',
  professional: 'Dark, serif, established — commercial feel.',
};

export function Website() {
  const { data, isLoading } = useWebsite();
  if (isLoading) return <Card className="card-pad"><Skeleton h={200} /></Card>;
  return data ? <Editor site={data} /> : <CreateWizard />;
}

function CreateWizard() {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ businessName: 'Summit Air & Plumbing', industry: 'hvac', services: 'AC repair, Heating, Water heaters, Drain cleaning', phone: '(512) 555-0100', city: 'Austin', slug: 'summit-air' });
  const [templateId, setTemplateId] = useState<WebsiteTemplate>('modern');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      await api.post('/website', {
        slug: f.slug, templateId, businessName: f.businessName,
        industryLabel: label(f.industry), services: f.services.split(',').map((s) => s.trim()).filter(Boolean),
        phone: f.phone, city: f.city,
      });
      qc.invalidateQueries({ queryKey: ['website'] });
      toast.push('Website created — customize and publish', 'success');
    } catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Could not create site', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="page-head"><div><div className="page-title">Build your website</div><div className="page-sub">A professional site on a custom domain in ~15 minutes</div></div></div>
      <Card className="card-pad" style={{ maxWidth: 820 }}>
        <div className="stack gap-4">
          <div className="grid grid-2">
            <Field label="Business name"><Input value={f.businessName} onChange={(e) => setF({ ...f, businessName: e.target.value })} /></Field>
            <Field label="Industry"><Select value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })}>{INDUSTRIES.map((i) => <option key={i} value={i}>{label(i)}</option>)}</Select></Field>
          </div>
          <Field label="Services (comma separated)"><Input value={f.services} onChange={(e) => setF({ ...f, services: e.target.value })} /></Field>
          <div className="grid grid-3">
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="City"><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
            <Field label="Site address" hint="worksy360.com/site/…"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} /></Field>
          </div>
          <div>
            <div className="field-label" style={{ marginBottom: 8 }}>Choose a template</div>
            <div className="grid grid-3">
              {WEBSITE_TEMPLATES.map((t) => (
                <div key={t} className="card card-pad" style={{ cursor: 'pointer', borderColor: templateId === t ? 'var(--color-primary)' : undefined }} onClick={() => setTemplateId(t)}>
                  <div className="row between"><strong style={{ textTransform: 'capitalize' }}>{t}</strong>{templateId === t && <Badge tone="info">Selected</Badge>}</div>
                  <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 6 }}>{TEMPLATE_DESC[t]}</p>
                </div>
              ))}
            </div>
          </div>
          <Button size="lg" loading={saving} onClick={create} disabled={!f.businessName || !f.slug}>Create my website →</Button>
        </div>
      </Card>
    </>
  );
}

function Editor({ site }: { site: Site }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Site>(site);
  const [saving, setSaving] = useState<string | null>(null);
  useEffect(() => setDraft(site), [site.updatedAt]);

  const patchBrand = (k: string, v: string) => setDraft((d) => ({ ...d, brand: { ...d.brand, [k]: v } }));
  const setHero = (k: string, v: string) => setDraft((d) => ({ ...d, sections: d.sections.map((s) => s.type === 'hero' ? { ...s, props: { ...s.props, [k]: v } } : s) }));
  const toggleSection = (id: string) => setDraft((d) => ({ ...d, sections: d.sections.map((s) => s.id === id ? { ...s, enabled: !s.enabled } : s) }));
  const heroProps = (draft.sections.find((s) => s.type === 'hero')?.props ?? {}) as Record<string, string>;

  const save = async (publish: boolean) => {
    setSaving(publish ? 'publish' : 'save');
    try {
      await api.put('/website', { templateId: draft.templateId, brand: draft.brand, sections: draft.sections });
      if (publish) await api.post('/website/publish');
      qc.invalidateQueries({ queryKey: ['website'] });
      toast.push(publish ? 'Website published! 🎉' : 'Saved', 'success');
    } catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Save failed', 'error'); }
    finally { setSaving(null); }
  };

  const liveUrl = `/api/site/${draft.slug}`;

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Website</div><div className="page-sub">{draft.status === 'published' ? <>Live at <a href={liveUrl} target="_blank" rel="noreferrer">/site/{draft.slug}</a></> : 'Draft — publish when ready'}</div></div>
        <div className="row gap-2">
          <Badge tone={draft.status === 'published' ? 'success' : 'warning'}>{label(draft.status)}</Badge>
          {draft.status === 'published' && <a href={liveUrl} target="_blank" rel="noreferrer"><Button variant="secondary">View live ↗</Button></a>}
          <Button variant="secondary" loading={saving === 'save'} onClick={() => save(false)}>Save</Button>
          <Button loading={saving === 'publish'} onClick={() => save(true)}>Publish</Button>
        </div>
      </div>

      <div className="sched" style={{ gridTemplateColumns: '340px 1fr' }}>
        <Card className="card-pad">
          <div className="stack gap-4">
            <Field label="Template"><Select value={draft.templateId} onChange={(e) => setDraft((d) => ({ ...d, templateId: e.target.value as WebsiteTemplate }))}>{WEBSITE_TEMPLATES.map((t) => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}</Select></Field>
            <div className="row gap-3">
              <Field label="Primary"><input type="color" value={draft.brand.primaryColor} onChange={(e) => patchBrand('primaryColor', e.target.value)} style={{ width: 48, height: 38, border: 'none', background: 'none' }} /></Field>
              <Field label="Accent"><input type="color" value={draft.brand.accentColor} onChange={(e) => patchBrand('accentColor', e.target.value)} style={{ width: 48, height: 38, border: 'none', background: 'none' }} /></Field>
            </div>
            <Field label="Business name"><Input value={draft.brand.businessName} onChange={(e) => patchBrand('businessName', e.target.value)} /></Field>
            <Field label="Phone"><Input value={draft.brand.phone ?? ''} onChange={(e) => patchBrand('phone', e.target.value)} /></Field>
            <Field label="Hero headline"><Input value={heroProps.headline ?? ''} onChange={(e) => setHero('headline', e.target.value)} /></Field>
            <Field label="Hero subtext"><Input value={heroProps.sub ?? ''} onChange={(e) => setHero('sub', e.target.value)} /></Field>
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Sections</div>
              {draft.sections.map((s) => (
                <label key={s.id} className="row gap-2" style={{ padding: '4px 0', fontSize: 'var(--text-sm)', textTransform: 'capitalize' }}>
                  <input type="checkbox" checked={s.enabled} onChange={() => toggleSection(s.id)} /> {s.type}
                </label>
              ))}
            </div>
          </div>
        </Card>

        <Card style={{ overflow: 'hidden' }}>
          <div className="card-head"><span className="card-title">Live preview</span><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Updates as you edit</span></div>
          <iframe title="Website preview" srcDoc={renderWebsiteHtml(draft)} style={{ width: '100%', height: 620, border: 0 }} />
        </Card>
      </div>
    </>
  );
}
